import { Schema } from 'effect';

// Expo Updates protocol v1 — https://docs.expo.dev/technical-specs/expo-updates-1/
// Shared between the manifest endpoint (apps/api/src/api-impl/mobile-updates-endpoints.ts)
// and the publish script (scripts/mobile/publish-ota.ts): the script writes
// these shapes to S3, the endpoint reads them back and re-encodes them.
//
// These schemas are therefore the AUTHORITATIVE field list in both
// directions, not just a description of what the script writes. See the
// UpdateManifest annotation below.

export const OTA_CHANNELS = ['staging', 'production'] as const;
export const OtaChannel = Schema.Literals([...OTA_CHANNELS]);
export type OtaChannel = typeof OtaChannel.Type;

export const OTA_PLATFORMS = ['ios', 'android'] as const;
export const OtaPlatform = Schema.Literals([...OTA_PLATFORMS]);
export type OtaPlatform = typeof OtaPlatform.Type;

/**
 * The `expo-runtime-version` header, which the manifest endpoint takes
 * straight off a PUBLIC, UNAUTHENTICATED request and then uses two ways: as a
 * path segment in `pointerKey`/`manifestKey`, and as the key into the pointer
 * cache — which caches MISSES too. Unvalidated, any stranger can burn one S3
 * GET and one cache slot per distinct junk value, evicting the handful of real
 * pointers and putting every genuine update check back on S3. Rejecting here
 * costs a regex instead.
 *
 * `{policy: 'fingerprint'}` (apps/mobile/app.config.ts) produces a 40-char
 * lowercase hex hash. The wider character set and length ceiling leave room
 * for a policy change (semver, `appVersion`) without another deploy; the
 * leading-alphanumeric rule keeps `.`/`..`-shaped values out of object keys.
 */
export const OtaRuntimeVersion = Schema.String.pipe(
  Schema.check(Schema.isPattern(/^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/)),
).annotate({
  identifier: 'OtaRuntimeVersion',
  description:
    'Expo runtime version (fingerprint hash). Validated before it reaches an S3 key or the pointer cache.',
});
export type OtaRuntimeVersion = typeof OtaRuntimeVersion.Type;

/** An asset entry in the protocol manifest. `hash` is base64url-encoded
 * SHA-256 of the file bytes; `key` is the asset's stable identity within the
 * update; `url` is absolute (CloudFront), so the server never rewrites it. */
export const ManifestAsset = Schema.Struct({
  hash: Schema.String,
  key: Schema.String,
  contentType: Schema.String,
  fileExtension: Schema.optional(Schema.String),
  url: Schema.String,
});
export type ManifestAsset = typeof ManifestAsset.Type;

export const UpdateManifest = Schema.Struct({
  id: Schema.String.check(Schema.isUUID()),
  createdAt: Schema.String,
  runtimeVersion: Schema.String,
  launchAsset: ManifestAsset,
  assets: Schema.Array(ManifestAsset),
  metadata: Schema.Record(Schema.String, Schema.String),
  extra: Schema.Record(Schema.String, Schema.Unknown),
}).annotate({
  identifier: 'ExpoManifest',
  title: 'Expo Updates manifest',
  description:
    'The JSON body of the `manifest` part in a multipart/mixed manifest response. ' +
    'NOT served verbatim: the endpoint decodes the S3 object against this schema and ' +
    're-encodes it exactly once, then signs those bytes. This schema is therefore the ' +
    'authoritative field list — a key added to the manifest in S3 without a matching ' +
    'field here is silently dropped on re-encode and never reaches the device.',
});
export type UpdateManifest = typeof UpdateManifest.Type;

/** The `directive` part body (protocol v1). Only `noUpdateAvailable` is
 * produced: rollback is pointer repointing, not `rollBackToEmbedded`. */
export const ExpoDirective = Schema.Struct({
  type: Schema.Literal('noUpdateAvailable'),
}).annotate({
  identifier: 'ExpoDirective',
  title: 'Expo Updates directive',
  description:
    'The JSON body of the `directive` part, returned when the device is already ' +
    'on the pointer head, no pointer exists, or a staged rollout put this device ' +
    'in the holdback bucket.',
});
export type ExpoDirective = typeof ExpoDirective.Type;

export const PointerHistoryEntry = Schema.Struct({
  updateId: Schema.String.check(Schema.isUUID()),
  createdAt: Schema.String,
  version: Schema.String,
  gitSha: Schema.String,
});
export type PointerHistoryEntry = typeof PointerHistoryEntry.Type;

/** The commit point for a (channel, platform, runtimeVersion) triple. The
 * publish script rewrites it last (assets → manifests → pointer), so a
 * pointer never references missing objects. Rollback = rewrite with a prior
 * history entry (`repoint`). */
export const UpdatePointer = Schema.Struct({
  updateId: Schema.String.check(Schema.isUUID()),
  rolloutPercent: Schema.Finite.pipe(
    Schema.check(Schema.isBetween({ minimum: 0, maximum: 100 })),
  ),
  previousUpdateId: Schema.NullOr(Schema.String.check(Schema.isUUID())),
  history: Schema.Array(PointerHistoryEntry),
  updatedAt: Schema.String,
});
export type UpdatePointer = typeof UpdatePointer.Type;

/** Update metadata sibling of the manifest — operational bookkeeping only
 * (release-rings.md: "every production OTA is a real changeset release"). */
export const UpdateMeta = Schema.Struct({
  version: Schema.String,
  gitSha: Schema.String,
  createdAt: Schema.String,
  channel: OtaChannel,
  platform: OtaPlatform,
  runtimeVersion: Schema.String,
});
export type UpdateMeta = typeof UpdateMeta.Type;

// Directive part body (protocol v1), pre-serialized: directives are signed,
// and the signature must cover the exact bytes on the wire. rollBackToEmbedded
// is intentionally not implemented in v1 — rollback is pointer repointing.
export const NO_UPDATE_AVAILABLE_BODY = '{"type":"noUpdateAvailable"}';

// Assets live under the mobile-ota/ prefix because the site CloudFront
// forwards the full /mobile-ota/assets/<sha> request path to S3 as the key
// (no path stripping — same trick as /desktop/channels/*). Pointers and
// updates sit outside the prefix, so the CDN can never serve them.
export const assetsKey = (sha256Hex: string) =>
  `mobile-ota/assets/${sha256Hex}`;

export const manifestKey = (
  channel: OtaChannel,
  platform: OtaPlatform,
  runtimeVersion: string,
  updateId: string,
) =>
  `updates/${channel}/${platform}/${runtimeVersion}/${updateId}/manifest.json`;

export const metaKey = (
  channel: OtaChannel,
  platform: OtaPlatform,
  runtimeVersion: string,
  updateId: string,
) => `updates/${channel}/${platform}/${runtimeVersion}/${updateId}/meta.json`;

export const pointerKey = (
  channel: OtaChannel,
  platform: OtaPlatform,
  runtimeVersion: string,
) => `pointers/${channel}/${platform}/${runtimeVersion}.json`;
